// Section 24.10: step(state, decision) -> { state, report, prompts } and resumeStep. The sim is
// a pure function (24.1): no I/O, no clock, all randomness from the seeded streams in state.
import { produce } from 'immer';
import { config, type Config } from '../config';
import { registryFor } from '../content';
import { SimError } from '../core/errors';
import { assertInvariants } from '../core/invariants';
import { runSeason } from '../core/runner';
import type {
  Decision,
  GameState,
  Phase,
  Prompt,
  PromptAnswer,
  PromptId,
  SeasonReport,
} from '../core/types';
import { phaseOrder } from '../phases/phase-order';
import { defaultCheckInvariants } from './context';
import { validateDecision } from './validate-decision';

export interface StepOptions {
  /** Config to resolve with; saves carry a snapshot (ADR-0008). Defaults to the current config. */
  cfg?: Config;
  /** Assert invariants after every phase. Defaults to on in dev and test builds. */
  checkInvariants?: boolean;
  /** Override the phase list (tests only). */
  phases?: readonly Phase[];
}

export interface StepResult {
  state: GameState;
  /** The season's report, or null while paused. */
  report: SeasonReport | null;
  /** Pausing prompts (answer with resumeStep) while paused; otherwise the open prompts the next Decision must answer. */
  prompts: Prompt[];
}

function resultOf(state: GameState): StepResult {
  if (state.phaseCursor !== null) return { state, report: null, prompts: state.pendingPrompts };
  return {
    state,
    report: state.history[state.history.length - 1] ?? null,
    prompts: state.openPrompts,
  };
}

function run(state: GameState, opts: StepOptions): GameState {
  const cfg = opts.cfg ?? config;
  return runSeason(state, {
    cfg,
    registry: registryFor(cfg),
    phases: opts.phases ?? phaseOrder,
    mode: 'live',
    checkInvariants: opts.checkInvariants ?? defaultCheckInvariants(),
  });
}

export function step(state: GameState, decision: Decision, opts: StepOptions = {}): StepResult {
  const cfg = opts.cfg ?? config;
  if (state.outcome !== null && !state.sandbox)
    throw new SimError('bad_status', 'the game has ended', { turn: state.turn });
  if (state.phaseCursor !== null)
    throw new SimError('bad_status', 'the season is paused; call resumeStep', { turn: state.turn });
  const problems = validateDecision(state, decision, cfg);
  if (problems.length > 0) {
    throw new SimError(
      'invalid_decision',
      problems.map((p) => `${p.path}: ${p.message}`).join('; '),
      { turn: state.turn, problems },
    );
  }
  const started = produce(state, (d) => {
    d.turn += 1;
    d.decisionLog.push(decision);
  });
  const next = run(started, opts);
  if (next.phaseCursor === null && (opts.checkInvariants ?? defaultCheckInvariants())) {
    assertInvariants(next, { cfg, registry: registryFor(cfg), previous: state, phase: 'step' });
  }
  return resultOf(next);
}

/**
 * Answer a pausing prompt and continue the season from `phaseCursor` (24.4). The answer is also
 * recorded in the season's logged Decision so replay supplies it up front.
 */
export function resumeStep(
  state: GameState,
  promptId: PromptId,
  answer: PromptAnswer,
  opts: StepOptions = {},
): StepResult {
  if (state.phaseCursor === null)
    throw new SimError('bad_status', 'the season is not paused', { turn: state.turn });
  const prompt = state.pendingPrompts.find((p) => p.id === promptId);
  if (prompt === undefined)
    throw new SimError('unknown_id', `no pending prompt ${promptId}`, {
      turn: state.turn,
      ids: [promptId],
    });
  if (prompt.kind !== answer.kind) {
    throw new SimError(
      'invalid_decision',
      `answer kind ${answer.kind} does not match prompt kind ${prompt.kind}`,
      { turn: state.turn },
    );
  }
  const answered = produce(state, (d) => {
    d.promptAnswers[promptId] = answer;
    const logged = d.decisionLog[d.decisionLog.length - 1];
    if (logged !== undefined) logged.promptAnswers[promptId] = answer;
  });
  return resultOf(run(answered, opts));
}
